import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { DragDropContext, Draggable } from '@hello-pangea/dnd';
import StrictModeDroppable from '../components/StrictModeDroppable';
import PlaceAutocomplete from '../components/PlaceAutocomplete';
import Header from '../components/Header';
import { UserCircleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Chatbot from '../components/Chatbot';
import ExpenseGroup from '../components/ExpenseGroup';
import ExpenseForm from '../components/ExpenseForm';
import AdScript from '../components/AdScript';

export default function TripPage({ isNew = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tripTitle, setTripTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [placeToVisit, setPlaceToVisit] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('destinations');
  const mapRef = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [notes, setNotes] = useState([]);
  const [destinationDays, setDestinationDays] = useState({});
  const [tips, setTips] = useState(null);
  const [isGeneratingTips, setIsGeneratingTips] = useState(false);
  const [selectedDestinationForStays, setSelectedDestinationForStays] = useState('');
  const [stays, setStays] = useState(null);
  const [isGeneratingStays, setIsGeneratingStays] = useState(false);
  const [selectedDestinationForAttractions, setSelectedDestinationForAttractions] = useState('');
  const [attractions, setAttractions] = useState(null);
  const [isGeneratingAttractions, setIsGeneratingAttractions] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expenseGroups, setExpenseGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editExpense, setEditExpense] = useState({
    description: '',
    amount: '',
    currency: '',
    split_type: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false);
  const [generatedItinerary, setGeneratedItinerary] = useState('');
  const [isEditingItinerary, setIsEditingItinerary] = useState(false);
  const [isItineraryError, setIsItineraryError] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      fetchTripDetails();
    }
  }, [id, isNew]);

  useEffect(() => {
    if (trip) {
      setEditTitle(trip.title);
      setEditStartDate(trip.start_date);
      setEditEndDate(trip.end_date);
    }
  }, [trip]);

  useEffect(() => {
    if (!window.google || !mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 20.5937, lng: 78.9629 }, // Center of India
      zoom: 5,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: false,
    });

    setMap(map);
    setDirectionsService(directionsService);
    setDirectionsRenderer(directionsRenderer);

    // Clean up
    return () => {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
      }
    };
  }, [mapRef.current]);

  useEffect(() => {
    if (!map || !destinations.length) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    const newMarkers = destinations.map((dest, index) => {
      return new window.google.maps.Marker({
        position: { 
          lat: parseFloat(dest.latitude), 
          lng: parseFloat(dest.longitude) 
        },
        map,
        label: (index + 1).toString(),
        title: dest.name
      });
    });
    setMarkers(newMarkers);

    // Fit bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach(marker => bounds.extend(marker.getPosition()));
      map.fitBounds(bounds);
    }

    return () => {
      newMarkers.forEach(marker => marker.setMap(null));
    };
  }, [map, destinations]);

  useEffect(() => {
    if (!map || !destinations.length) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    const newMarkers = [];

    // Add markers for each destination
    destinations.forEach((dest, index) => {
      const marker = new window.google.maps.Marker({
        position: { lat: parseFloat(dest.latitude), lng: parseFloat(dest.longitude) },
        map: map,
        label: (index + 1).toString(),
        title: dest.name,
        animation: window.google.maps.Animation.DROP
      });
      newMarkers.push(marker);
    });

    setMarkers(newMarkers);

    // If we have at least 2 destinations, show the route
    if (destinations.length >= 2 && directionsService && directionsRenderer) {
      const waypoints = destinations.slice(1, -1).map(dest => ({
        location: { lat: parseFloat(dest.latitude), lng: parseFloat(dest.longitude) },
        stopover: true
      }));

      const origin = { 
        lat: parseFloat(destinations[0].latitude), 
        lng: parseFloat(destinations[0].longitude) 
      };
      const destination = { 
        lat: parseFloat(destinations[destinations.length - 1].latitude), 
        lng: parseFloat(destinations[destinations.length - 1].longitude) 
      };

      directionsService.route({
        origin,
        destination,
        waypoints,
        travelMode: 'DRIVING',
        optimizeWaypoints: true
      }, (result, status) => {
        if (status === 'OK') {
          directionsRenderer.setDirections(result);
        }
      });
    } else if (directionsRenderer) {
      directionsRenderer.setDirections({ routes: [] });
    }

    // Fit bounds to show all markers
    if (newMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      newMarkers.forEach(marker => bounds.extend(marker.getPosition()));
      map.fitBounds(bounds);
      if (newMarkers.length === 1) {
        map.setZoom(10);
      }
    }
  }, [destinations, map, directionsService, directionsRenderer]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserName(profile.first_name || profile.last_name || user.email);
        }
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!isNew && id) {
      fetchNotes();
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew && id) {
      fetchExpenseGroups();
    }
  }, [id, isNew]);

  const fetchTripDetails = async () => {
    try {
      const { data: tripData, error: tripError } = await supabase
        .from('trips')
        .select('*')
        .eq('id', id)
        .single();

      if (tripError) throw tripError;
      setTrip(tripData);

      const { data: destData, error: destError } = await supabase
        .from('destinations')
        .select('*')
        .eq('trip_id', id)
        .order('order', { ascending: true });

      if (destError) throw destError;
      setDestinations(destData);
      
      // Set the destinationDays from the stored stay_days
      const daysMap = {};
      destData.forEach(dest => {
        if (dest.stay_days) {
          daysMap[dest.id] = dest.stay_days;
        }
      });
      setDestinationDays(daysMap);
    } catch (error) {
      console.error('Error fetching trip details:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('trip_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchExpenseGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('expense_groups')
        .select('*')
        .eq('trip_id', id);
      
      if (error) throw error;
      setExpenseGroups(data);
    } catch (error) {
      console.error('Error fetching expense groups:', error);
    }
  };

  const fetchExpenses = async (groupId) => {
    try {
      const { data, error } = await supabase
        .from('expenses_table')  // Fixed: using correct table name
        .select('*')
        .eq('group_id', groupId);
      
      if (error) throw error;
      setExpenses(data || []); // Add fallback empty array
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]); // Reset expenses on error
    }
  };

  const handleViewExpenses = (group) => {
    setSelectedGroup(group);
    fetchExpenses(group.id); // Call fetchExpenses when group is selected
  };

  const handlePlaceSelect = async (place) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) throw new Error('No active session');

      if (!place || !place.geometry || !place.geometry.location) {
        throw new Error('Invalid place data');
      }

      const { error } = await supabase
        .from('destinations')
        .insert({
          trip_id: id,
          name: place.formatted_address || place.name,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          created_by: session.user.id,
          arrival_time: new Date().toISOString(),
          order: destinations.length + 1
        });

      if (error) throw error;
      fetchTripDetails();
    } catch (error) {
      console.error('Error adding destination:', error);
      setError(error.message);
    }
  };

  const getPlaceDetails = async (placeId) => {
    const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    return data.result;
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedDestinations = Array.from(destinations);
    const [removed] = reorderedDestinations.splice(result.source.index, 1);
    reorderedDestinations.splice(result.destination.index, 0, removed);

    // Optimistically update the UI
    setDestinations(reorderedDestinations);

    try {
      // Update each destination with its new order
      const updates = reorderedDestinations.map((dest, index) => ({
        id: dest.id,
        order: index + 1
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('destinations')
          .update({ order: update.order })
          .eq('id', update.id);

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error updating destinations order:', error);
      // Revert to original order on error
      fetchTripDetails();
    }
  };

  const handleDeleteDestination = async (id) => {
    try {
      const { error } = await supabase
        .from('destinations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchTripDetails();
    } catch (error) {
      console.error('Error deleting destination:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleEditTrip = async () => {
    try {
      const { error } = await supabase
        .from('trips')
        .update({
          title: editTitle,
          start_date: editStartDate,
          end_date: editEndDate
        })
        .eq('id', id);

      if (error) throw error;
      setShowEditModal(false);
      fetchTripDetails();
    } catch (error) {
      console.error('Error updating trip:', error);
    }
  };

  const generateItinerary = async (place, days, isNewTrip = false) => {
    try {
      let promptText;
      
      if (isNewTrip || (Array.isArray(place) && place.length === 1)) {
        // For new trips or single destination
        const location = typeof place === 'string' ? place : place[0].name;
        promptText = `Create a ${days}-day travel itinerary for ${location}. Format each day as:
Day X:
• Morning activities
• Afternoon activities
• Evening activities
Include specific places, timing, and activities.`;
      } else {
        // For multiple destinations
        const destinationsText = Array.isArray(place) 
          ? place.map(d => d.name).join(', ') 
          : place;

        const daysDistribution = Array.isArray(place) 
          ? place.map(d => ({
              name: d.name,
              days: destinationDays[d.id] || 1
            })) 
          : [];

        promptText = `Create a detailed ${days}-day travel itinerary covering these destinations: ${destinationsText}.
        
Distribution of days:
${daysDistribution.map(d => `${d.name}: ${d.days} day(s)`).join('\n')}

Format each day as:
Day X:
• Morning activities and timings
• Afternoon activities and timings
• Evening activities and timings

Include travel time between destinations, specific places to visit, and local highlights.`;
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: promptText }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 8192,
          }
        })
      });

      if (!response.ok) throw new Error('Failed to generate itinerary');
      
      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (!generatedText) throw new Error('No itinerary content generated');
      
      return generatedText;
    } catch (error) {
      console.error('Error generating itinerary:', error);
      throw error;
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    
    if (!tripTitle || !startDate || !endDate || !selectedPlace) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session) throw new Error('No active session');

      // Calculate number of days
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

      // Generate initial itinerary with isNewTrip flag
      const itinerary = await generateItinerary(
        selectedPlace.formatted_address || selectedPlace.name, 
        days,
        true // Add this flag
      );

      // Create the trip
      const { data: trip, error: tripError } = await supabase
        .from('trips')
        .insert({
          title: tripTitle,
          start_date: startDate,
          end_date: endDate,
          created_at: new Date().toISOString(),
          owner_id: session.user.id,
          location: selectedPlace.formatted_address || selectedPlace.name,
          description: itinerary
        })
        .select()
        .single();

      if (tripError) throw tripError;

      // Add initial destination
      const { error: destError } = await supabase
        .from('destinations')
        .insert({
          trip_id: trip.id,
          name: selectedPlace.formatted_address || selectedPlace.name,
          latitude: selectedPlace.geometry.location.lat(),
          longitude: selectedPlace.geometry.location.lng(),
          created_at: new Date().toISOString(),
          created_by: session.user.id,
          arrival_time: new Date(startDate).toISOString(),
          order: 1
        });

      if (destError) throw destError;

      navigate(`/trips/${trip.id}`);
    } catch (error) {
      console.error('Error creating trip:', error);
      setError(error.message || 'Failed to create trip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderTabs = () => {
    return (
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveDetailTab('destinations')}
            className={`${
              activeDetailTab === 'destinations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Destinations
          </button>
          <button
            onClick={() => setActiveDetailTab('itinerary')}
            className={`${
              activeDetailTab === 'itinerary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Itinerary
          </button>
          <button
            onClick={() => setActiveDetailTab('notes')}
            className={`${
              activeDetailTab === 'notes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Notes
          </button>
          <button
            onClick={() => setActiveDetailTab('tips')}
            className={`${
              activeDetailTab === 'tips'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Tips & Recommendations
          </button>
          <button
            onClick={() => setActiveDetailTab('stays')}
            className={`${
              activeDetailTab === 'stays'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Stays
          </button>
          <button
            onClick={() => setActiveDetailTab('attractions')}
            className={`${
              activeDetailTab === 'attractions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Popular Attractions
          </button>
          <button
            onClick={() => setActiveDetailTab('expenses')}
            className={`${
              activeDetailTab === 'expenses'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Expenses
          </button>
        </nav>
      </div>
    );
  };

  const handleStartNavigation = () => {
    if (destinations.length >= 2) {
      // Create Google Maps URL with waypoints
      const origin = `${destinations[0].latitude},${destinations[0].longitude}`;
      const destination = `${destinations[destinations.length - 1].latitude},${destinations[destinations.length - 1].longitude}`;
      const waypoints = destinations.slice(1, -1)
        .map(dest => `${dest.latitude},${dest.longitude}`)
        .join('|');

      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${
        waypoints ? `&waypoints=${waypoints}` : ''
      }&travelmode=driving`;

      // Open in new tab
      window.open(url, '_blank');
    }
  };

  const ItineraryTab = () => {
    const [editedItinerary, setEditedItinerary] = useState(trip?.description || '');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (generatedItinerary) {
        setEditedItinerary(generatedItinerary);
        setGeneratedItinerary(''); // Reset after using
      }
    }, [generatedItinerary]);

    const handleSave = async () => {
      try {
        setIsSaving(true);
        
        // Process the text to ensure proper formatting
        let formattedText = editedItinerary;

        // Remove any text before the first "Day" occurrence and rename Day 0 to Travel Suggestions
        formattedText = formattedText
          .replace(/^.*?(?=Day \d+:)/s, '')  // Remove intro text
          .replace(/Day 0:/, 'Travel Suggestions:');  // Rename Day 0

        // Clean up formatting
        formattedText = formattedText
          .replace(/\r\n/g, '\n')  // Normalize line endings
          .replace(/\n{3,}/g, '\n\n')  // Remove extra blank lines
          .split('\n')
          .map(line => {
            line = line.trim();
            // Format headers
            if (line.match(/^(Day \d+|Travel Suggestions):/)) {
              return `\n${line}`;  // Add extra newline before each day
            }
            // Format section headers
            if (line.match(/^(Journey Details|Morning Activities|Afternoon Activities|Evening Activities|Travel Tips):/)) {
              return `\n${line}`;  // Add extra newline before each section
            }
            // Handle bullet points
            if (line.match(/^[-•*]\s*/)) {
              return line.replace(/^[-•*]\s*/, '• ');
            }
            return line;
          })
          .join('\n')
          .trim();

        // Save to database
        const { error } = await supabase
          .from('trips')
          .update({ description: formattedText })
          .eq('id', id);

        if (error) throw error;
        
        setTrip(prev => ({ ...prev, description: formattedText }));
        setIsEditingItinerary(false);
      } catch (error) {
        console.error('Error saving itinerary:', error);
      } finally {
        setIsSaving(false);
      }
    };

    const formatItinerary = (text) => {
      if (!text) return null;
      
      try {
        let [mainContent, travelTips] = text.split(/travel tips:/i);
        
        // Define special sections
        const specialSections = [
          'Journey Details',
          'Morning Activities',
          'Afternoon Activities',
          'Evening Activities'
        ];

        // Clean up main content format
        mainContent = mainContent
          .replace(/^.*?(?=Day \d+:)/s, '')
          .replace(/\*+/g, '')  // Remove asterisks
          .replace(/^[•\-\*]\s*\n/gm, '')  // Remove bullet points followed by newline
          .replace(/\n{2,}/g, '\n')  // Replace multiple newlines with single newline
          .replace(/^\s+/gm, '')  // Remove leading spaces
          .replace(/(?<=:)\s*\n\s*([•\-\*])/g, ' $1')  // Join lines after colons with bullet points
          .replace(/[•\-\*]\s*$/gm, '')  // Remove trailing bullet points
          .replace(/\n\s*$/gm, '')  // Remove trailing blank lines
          .replace(/\s+$/gm, '')  // Remove trailing spaces
          .trim();

        // Split into sections and clean each section
        const sections = mainContent.split(/(?=Day \d+(?:-\d+)?:)/).map(section => {
          return section
            .split('\n')
            .filter(line => line.trim() && !line.match(/^[•\-\*]\s*$/))  // Remove empty bullets
            .join('\n')
            .trim();
        }).filter(Boolean);

        mainContent = sections.join('\n\n');

        // Clean bullet points inside sections
        mainContent = mainContent.replace(/(?<=:.*?)(?:\n\s*[•\-\*]\s*$)+/gm, '');  // Remove trailing bullets in lists

        // Rest of your existing code...
        const days = mainContent.split(/Day \d+(?:-\d+)?:/)
          .filter(Boolean)
          .map((content, index) => {
            const dayMatch = content.match(/(.+?)(?:\n|$)/);
            const title = dayMatch ? dayMatch[1].trim() : '';
            const sections = [];
            
            // Split and clean section content
            const sectionTexts = content
              .slice(dayMatch ? dayMatch[0].length : 0)
              .split(new RegExp(`(?=${specialSections.map(s => `${s}:`).join('|')})`))
              .filter(Boolean);

            sectionTexts.forEach(sectionText => {
              const [header, ...items] = sectionText.split('\n').map(s => s.trim()).filter(Boolean);
              if (header && items.length > 0) {
                sections.push({
                  header: header.replace(/:$/, ''),
                  isSpecial: specialSections.some(s => header.startsWith(s)),
                  items: items.map(item => {
                    // Clean up bullet points and format text
                    return item
                      .replace(/^[•-]\s*/, '')
                      .replace(/\*\*/g, '')
                      .trim();
                  })
                });
              }
            });

            return { title, sections };
          });

        const tipSections = [];
        if (travelTips) {
          const tipLines = travelTips.split('\n').map(line => line.trim()).filter(Boolean);
          let currentSection = null;
          let currentItems = [];

          tipLines.forEach(line => {
            if (line.endsWith(':')) {
              if (currentSection) {
                tipSections.push({
                  header: currentSection,
                  items: currentItems
                });
              }
              currentSection = line.slice(0, -1);
              currentItems = [];
            } else if (line.startsWith('•') || line.startsWith('-')) {
              currentItems.push(line.replace(/^[•-]\s*/, ''));
            }
          });

          if (currentSection && currentItems.length > 0) {
            tipSections.push({
              header: currentSection,
              items: currentItems
            });
          }
        }

        return (
          <div className="space-y-6">
            {days.map((day, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">
                    {index === 0 ? 'Travel Suggestions' : `Day ${index}: ${day.title}`}
                  </h3>
                </div>
                <div className="p-6 space-y-6">
                  {day.sections.map((section, sIdx) => (
                    <div key={sIdx} className="space-y-3">
                      <h4 className="text-lg font-bold text-gray-700 border-b border-gray-200 pb-2">
                        {section.header}
                      </h4>
                      <div className="grid gap-2 pl-4">
                        {section.items.map((item, iIdx) => (
                          <div key={iIdx} className="flex items-start gap-3">
                            <span className="text-blue-500 flex-shrink-0">•</span>
                            <span className="text-gray-600 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {tipSections.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white">Travel Tips</h3>
                </div>
                <div className="p-6 space-y-6">
                  {tipSections.map((section, index) => (
                    <div key={index} className="space-y-3">
                      <h4 className="text-lg font-bold text-gray-700 border-b border-gray-200 pb-2">
                        {section.header}
                      </h4>
                      <div className="grid gap-2 pl-4">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3">
                            <span className="text-green-500 flex-shrink-0">•</span>
                            <span className="text-gray-600 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      } catch (error) {
        console.error('Error formatting itinerary:', error);
        return <p>Error formatting itinerary</p>;
      }
    };

    const handleGenerateItinerary = async () => {
      try {
        setIsEditingItinerary(true);
        setIsSaving(true);
        const newItinerary = await generateItinerary(destinations, destinationDays);
        setEditedItinerary(newItinerary);
      } catch (error) {
        console.error('Error generating itinerary:', error);
      } finally {
        setIsSaving(false);
      }
    };

    if (!trip?.description && !isEditingItinerary) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">No itinerary available.</p>
          <button
            onClick={() => setIsEditingItinerary(true)}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Create Itinerary
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-black">Trip Itinerary</h2>
            <div className="flex space-x-3">
              {!isEditingItinerary ? (
                <>
                  <button
                    onClick={generateNewItinerary}
                    disabled={isGeneratingItinerary}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {isGeneratingItinerary ? 'Generating...' : 'Generate New'}
                  </button>
                  <button
                    onClick={() => {
                      setEditedItinerary(trip.description);
                      setIsEditingItinerary(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Edit
                  </button>
                </>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsEditingItinerary(false)}
                    className="text-gray-600 hover:text-gray-700 font-medium"
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
          {isItineraryError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">Failed to generate itinerary. Please try again.</p>
            </div>
          )}
          {isEditingItinerary ? (
            <textarea
              value={editedItinerary}
              onChange={(e) => setEditedItinerary(e.target.value)}
              className="w-full h-96 p-4 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your itinerary details. Format: 
Day 1
Your day 1 activities

Day 2
Your day 2 activities"
            />
          ) : (
            <div className="space-y-4">
              {formatItinerary(trip.description)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const NotesTab = () => {
    const [newNote, setNewNote] = useState('');
    const [newNoteTitle, setNewNoteTitle] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [editText, setEditText] = useState('');
    const [editTitle, setEditTitle] = useState('');

    const handleAddNote = async (e) => {
      e.preventDefault();
      if (!newNote.trim() || !newNoteTitle.trim()) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const newNoteData = {
          title: newNoteTitle.trim(),
          content: newNote.trim(),
          trip_id: id,
          created_at: new Date().toISOString()
        };

        const { data, error } = await supabase
          .from('notes')
          .insert(newNoteData)
          .select()
          .maybeSingle();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        // Update local state
        if (data) {
          setNotes(prevNotes => [data, ...prevNotes]);
          setNewNote('');
          setNewNoteTitle('');
        }

      } catch (error) {
        console.error('Error details:', error);
        alert('Failed to add note. Please try again.');
      }
    };

    const handleUpdateNote = async (noteId) => {
      try {
        const { error } = await supabase
          .from('notes')
          .update({
            content: editText,
            title: editTitle
          })
          .eq('id', noteId);

        if (error) throw error;
        setNotes(notes.map(note => 
          note.id === noteId ? { ...note, content: editText, title: editTitle } : note
        ));
        setEditingNote(null);
      } catch (error) {
        console.error('Error updating note:', error);
      }
    };

    const handleDeleteNote = async (noteId) => {
      if (!window.confirm('Are you sure you want to delete this note?')) return;

      try {
        const { error } = await supabase
          .from('notes')
          .delete()
          .eq('id', noteId);

        if (error) throw error;
        setNotes(notes.filter(note => note.id !== noteId));
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    };

    return (
      <div className="space-y-6">
        <form onSubmit={handleAddNote} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Title
            </label>
            <input
              type="text"
              value={newNoteTitle}
              onChange={(e) => setNewNoteTitle(e.target.value)}
              placeholder="Enter note title..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Content
            </label>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add your note content..."
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              required
            />
          </div>

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Add Note
          </button>
        </form>

        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="p-4 bg-gray-50 rounded-lg">
              {editingNote === note.id ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Edit title..."
                  />
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    rows="3"
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => setEditingNote(null)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleUpdateNote(note.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 mb-2">{note.title}</h3>
                      <p className="whitespace-pre-wrap text-gray-700">{note.content}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setEditingNote(note.id);
                          setEditText(note.content);
                          setEditTitle(note.title);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const generateTips = async () => {
    setIsGeneratingTips(true);
    try {
      const promptText = `Create brief, essential travel tips for ${destinations.map(d => d.name).join(', ')}. Format as follows:

Essentials:
• 2-3 key documents/items to carry
• Important local emergency numbers
• Must-have apps for the trip

Weather & Packing:
• Current weather overview
• 3-4 essential clothing items
• Key accessories needed

Local Know-How:
• 2-3 cultural tips
• Basic phrases to know
• Local transport basics

Safety & Health:
• Key precautions
• Nearby medical facilities
• Important health tips

Keep each bullet point brief and actionable. Avoid lengthy explanations.`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: promptText }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 4096,
          }
        })
      });

      if (!response.ok) throw new Error('Failed to generate tips');
      
      const data = await response.json();
      const generatedTips = data.candidates?.[0]?.content?.parts?.[0]?.text;

      // Clean up the generated text
      const cleanedTips = generatedTips
        .replace(/^[•-]\s*/gm, '• ')  // Standardize bullet points
        .replace(/\n{3,}/g, '\n\n')   // Remove extra line breaks
        .trim();

      // Save tips to the database
      const { error } = await supabase
        .from('trips')
        .update({ travel_tips: cleanedTips })
        .eq('id', id);

      if (error) throw error;
      setTips(cleanedTips);
    } catch (error) {
      console.error('Error generating tips:', error);
    } finally {
      setIsGeneratingTips(false);
    }
  };

  const TipsTab = () => {
    return (
      <div className="space-y-6">
        {!tips && !isGeneratingTips ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No travel tips available yet.</p>
            <button
              onClick={generateTips}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Generate Tips
            </button>
          </div>
        ) : isGeneratingTips ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-gray-600">Generating recommendations...</span>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Travel Tips
              </h3>
            </div>
            <div className="p-6">
              {tips.split('\n\n').map((section, index) => {
                // Clean the section text to remove asterisks and extra spaces
                const cleanedSection = section
                  .replace(/\*\*/g, '')  // Remove asterisks
                  .trim();
                const [header, ...items] = cleanedSection.split('\n');
                if (!header || !items.length) return null;
                
                return (
                  <div key={index} className="mb-6 last:mb-0">
                    <h4 className="text-lg font-bold text-gray-700 border-b border-gray-200 pb-2 mb-3">
                      {header.replace(/:/g, '')}
                    </h4>
                    <div className="grid gap-2">
                      {items
                        .filter(Boolean)
                        .map((item, itemIndex) => {
                          // Clean up bullet points and extra spaces
                          const cleanedItem = item.replace(/^[•-]\s*/, '').trim();
                          return (
                            <div key={itemIndex} className="flex items-start gap-2">
                              <span className="text-blue-500 flex-shrink-0">•</span>
                              <span className="text-gray-600">{cleanedItem}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={generateTips}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Regenerate Tips
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const generateStays = async (destination) => {
    setIsGeneratingStays(true);
    try {
      const promptText = `Generate hotel recommendations for ${destination} in the following format exactly:

3-Star Hotels
• Hotel Name: Brief description, price range, key amenities, location highlight
• Hotel Name: Brief description, price range, key amenities, location highlight

4-Star Hotels
• Hotel Name: Brief description, price range, key amenities, location highlight
• Hotel Name: Brief description, price range, key amenities, location highlight

5-Star Hotels
• Hotel Name: Brief description, price range, key amenities, location highlight
• Hotel Name: Brief description, price range, key amenities, location highlight

Keep descriptions concise. Include price ranges in $/night. Focus on key features that matter to travelers.`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: promptText }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 4096,
          }
        })
      });

      if (!response.ok) throw new Error('Failed to generate stays');
      
      const data = await response.json();
      let generatedStays = data.candidates?.[0]?.content?.parts?.[0]?.text;

      // Clean up the text - remove asterisks and standardize bullet points
      generatedStays = generatedStays
        .replace(/\*\*/g, '')  // Remove all asterisks
        .replace(/^\*/gm, '•')  // Replace starting asterisks with bullet points
        .replace(/\n{3,}/g, '\n\n')  // Normalize line spacing
        .trim();

      setStays(generatedStays);
    } catch (error) {
      console.error('Error generating stays:', error);
    } finally {
      setIsGeneratingStays(false);
    }
  };

  const StaysTab = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <select
            value={selectedDestinationForStays}
            onChange={(e) => setSelectedDestinationForStays(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md p-2"
          >
            <option value="">Select Destination</option>
            {destinations.map((dest) => (
              <option key={dest.id} value={dest.name}>
                {dest.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => generateStays(selectedDestinationForStays)}
            disabled={!selectedDestinationForStays || isGeneratingStays}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            Check Stays
          </button>
        </div>

        {isGeneratingStays ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-gray-600">Finding hotels...</span>
          </div>
        ) : stays ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-blue-600 px-6 py-3">
              <h3 className="text-lg font-bold text-white">Stays</h3>
            </div>
            <div className="p-6">
              {stays.split('\n\n').map((section, sectionIndex) => {
                const [header, ...hotels] = section.split('\n');
                if (!header || !hotels.length) return null;

                return (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      {header}
                    </h4>
                    <div className="space-y-4">
                      {hotels.map((hotel, hotelIndex) => {
                        const cleanedHotel = hotel.replace(/^[•-]\s*/, '');
                        const [hotelName, ...description] = cleanedHotel.split(':');
                        return (
                          <div key={hotelIndex} className="pl-4 border-l-2 border-blue-200">
                            <p className="text-gray-700">
                              <strong>{hotelName}:</strong> 
                              {description.join(':')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const generateAttractions = async (destination) => {
    setIsGeneratingAttractions(true);
    try {
      const promptText = `Generate popular tourist attractions for ${destination} in the following format exactly:

Must-Visit Attractions
• Attraction Name: Brief description, entry fee/price range, timing details, key highlights
• Attraction Name: Brief description, entry fee/price range, timing details, key highlights

Historical Sites
• Site Name: Brief description, entry fee/price range, timing details, historical significance
• Site Name: Brief description, entry fee/price range, timing details, historical significance

Local Experiences
• Experience Name: Brief description, price range, timing details, what to expect
• Experience Name: Brief description, price range, timing details, what to expect

Keep descriptions concise. Include actual price ranges when possible. Focus on practical visitor information.`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: promptText }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 4096,
          }
        })
      });

      if (!response.ok) throw new Error('Failed to generate attractions');
      
      const data = await response.json();
      let generatedAttractions = data.candidates?.[0]?.content?.parts?.[0]?.text;

      // Clean up the text
      generatedAttractions = generatedAttractions
        .replace(/\*\*/g, '')  // Remove all asterisks
        .replace(/^\*/gm, '•')  // Replace starting asterisks with bullet points
        .replace(/\n{3,}/g, '\n\n')  // Normalize line spacing
        .trim();

      setAttractions(generatedAttractions);
    } catch (error) {
      console.error('Error generating attractions:', error);
    } finally {
      setIsGeneratingAttractions(false);
    }
  };

  const AttractionsTab = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <select
            value={selectedDestinationForAttractions}
            onChange={(e) => setSelectedDestinationForAttractions(e.target.value)}
            className="flex-1 border border-gray-300 rounded-md p-2"
          >
            <option value="">Select Destination</option>
            {destinations.map((dest) => (
              <option key={dest.id} value={dest.name}>
                {dest.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => generateAttractions(selectedDestinationForAttractions)}
            disabled={!selectedDestinationForAttractions || isGeneratingAttractions}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
          >
            Show Attractions
          </button>
        </div>

        {isGeneratingAttractions ? (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-gray-600">Finding attractions...</span>
          </div>
        ) : attractions ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-blue-600 px-6 py-3">
              <h3 className="text-lg font-bold text-white">Popular Attractions</h3>
            </div>
            <div className="p-6">
              {attractions.split('\n\n').map((section, sectionIndex) => {
                const [header, ...items] = section.split('\n');
                if (!header || !items.length) return null;

                return (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      {header}
                    </h4>
                    <div className="space-y-4">
                      {items.map((item, itemIndex) => {
                        const cleanedItem = item.replace(/^[•-]\s*/, '');
                        const [attractionName, ...description] = cleanedItem.split(':');
                        return (
                          <div key={itemIndex} className="pl-4 border-l-2 border-blue-200">
                            <p className="text-gray-700">
                              <strong>{attractionName}:</strong> 
                              {description.join(':')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderDetailTabContent = () => {
    switch (activeDetailTab) {
      case 'destinations': {
        const tripDays = trip ? Math.ceil((new Date(trip.end_date) - new Date(trip.start_date)) / (1000 * 60 * 60 * 24)) : 0;
        return (
          <div className="space-y-6">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Destinations</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={handlePlaceSelect}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Add Destination
                    </button>
                  </div>
                </div>
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Destination
                    </label>
                    <PlaceAutocomplete onPlaceSelect={handlePlaceSelect} />
                  </div>
                  {destinations.length === 0 ? (
                    <p className="text-gray-500">No destinations added yet.</p>
                  ) : (
                    <>
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <StrictModeDroppable droppableId="destinations">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="space-y-2"
                            >
                              {destinations.map((dest, index) => (
                                <Draggable
                                  key={dest.id}
                                  draggableId={dest.id.toString()}
                                  index={index}
                                >
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <span className="w-6 h-6 flex items-center justify-center bg-blue-600 text-white rounded-full text-sm">
                                          {index + 1}
                                        </span>
                                        <span>{dest.name}</span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        {index > 0 && tripDays > 0 && (
                                          <select
                                            value={destinationDays[dest.id] || 1}
                                            onChange={(e) => handleDestinationDaysChange(dest.id, Number(e.target.value))}
                                            className="border border-gray-300 rounded p-1 pr-8 appearance-none bg-white"
                                          >
                                            {Array.from({ length: tripDays }, (_, i) => (
                                              <option value={i + 1} key={i + 1}>
                                                {i + 1}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                        <button
                                          onClick={() => handleDeleteDestination(dest.id)}
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </StrictModeDroppable>
                      </DragDropContext>
                      <div className="mt-4">
                        <button
                          onClick={handleStartNavigation}
                          disabled={destinations.length < 2}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white ${
                            destinations.length < 2
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                          }`}
                        >
                          Start Navigation
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg overflow-hidden shadow">
              <div 
                ref={mapRef} 
                className="w-full h-[500px]"
                style={{ 
                  visibility: activeDetailTab === 'destinations' ? 'visible' : 'hidden',
                  position: 'relative'
                }}
              />
            </div>
          </div>
        );
      }
      case 'itinerary':
        return <ItineraryTab />;
      case 'notes':
        return <NotesTab />;
      case 'tips':
        return <TipsTab />;
      case 'stays':
        return <StaysTab />;
      case 'attractions':
        return <AttractionsTab />;
      case 'expenses':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-7xl mx-auto">
            <ExpenseGroup
              tripId={id}
              onGroupCreated={(group) => {
                setExpenseGroups([...expenseGroups, group]);
              }}
              onGroupDeleted={handleDeleteGroup}
              onGroupEdited={(updatedGroup) => {
                setExpenseGroups(expenseGroups.map(g => 
                  g.id === updatedGroup.id ? updatedGroup : g
                ));
              }}
            />
            
            {expenseGroups.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium mb-4">Expense Groups</h3>
                <div className="space-y-4">
                  {expenseGroups.map((group) => (
                    <div
                      key={group.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      {editingGroupId === group.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editGroupName}
                            onChange={(e) => setEditGroupName(e.target.value)}
                            className="border rounded px-2 py-1"
                          />
                          <button
                            onClick={() => handleUpdateGroup(group.id)}
                            className="bg-green-500 text-white px-2 py-1 rounded"
                          >
                            Save
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-medium">{group.group_name}</h4>
                          <p className="text-sm text-gray-500">
                            {group.members.length} members
                          </p>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewExpenses(group)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          View Expenses
                        </button>
                        <button
                          onClick={() => handleEditGroup(group)}
                          className="text-yellow-600 hover:text-yellow-700"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedGroup && (
              <ExpenseForm
                groupId={selectedGroup.id}
                members={selectedGroup.members}
                onExpenseAdded={(expense) => {
                  setExpenses([...expenses, expense]);
                }}
              />
            )}

            {expenses.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium mb-4">Expenses</h3>
                <div className="space-y-4">
                  {expenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      {editingExpenseId === expense.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={editExpense.description}
                            onChange={(e) => setEditExpense({...editExpense, description: e.target.value})}
                            className="border rounded px-2 py-1"
                            placeholder="Description"
                          />
                          <input
                            type="number"
                            value={editExpense.amount}
                            onChange={(e) => setEditExpense({...editExpense, amount: e.target.value})}
                            className="border rounded px-2 py-1 w-24"
                          />
                          <input
                            type="text"
                            value={editExpense.currency}
                            onChange={(e) => setEditExpense({...editExpense, currency: e.target.value})}
                            className="border rounded px-2 py-1 w-20"
                          />
                          <select
                            value={editExpense.split_type}
                            onChange={(e) => setEditExpense({...editExpense, split_type: e.target.value})}
                            className="border rounded px-2 py-1"
                          >
                            <option value="equal">Equal</option>
                            <option value="percentage">Percentage</option>
                            <option value="custom">Custom</option>
                          </select>
                          <button
                            onClick={() => handleUpdateExpense(expense.id)}
                            className="bg-green-500 text-white px-2 py-1 rounded"
                          >
                            Save
                          </button>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-medium">{expense.description}</h4>
                          <p className="text-sm text-gray-500">
                            Amount: {expense.currency} {expense.amount}
                          </p>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <div className="text-sm text-gray-500">
                          Split: {expense.split_type}
                        </div>
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="text-yellow-600 hover:text-yellow-700"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedGroup && expenses.length > 0 && (
              <div className="col-span-2 bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-medium mb-4">Expense Summary</h3>
                {(() => {
                  const summaries = calculateBalances(expenses, selectedGroup.members);
                  return (
                    <div className="grid gap-6">
                      {Object.entries(summaries).map(([currency, { totalExpenditure, balances, settlements }]) => (
                        <div key={currency} className="border rounded-lg p-4 bg-gray-50">
                          <h4 className="text-xl font-bold text-blue-600 mb-4">
                            {currency} Summary
                          </h4>
                          
                          <div className="text-lg font-semibold mb-6">
                            Total Spent: {currency} {totalExpenditure.toFixed(2)}
                          </div>

                          <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-3">
                              <h5 className="font-semibold text-gray-700">Member Balances</h5>
                              <div className="bg-white rounded-lg p-3 space-y-2">
                                {Object.entries(balances).map(([email, data]) => (
                                  <div key={email} className="space-y-1 border-b pb-2">
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium">{data.name}</span>
                                      <span className={`font-medium ${data.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        Net: {data.net >= 0 ? '+' : ''}{data.net.toFixed(2)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-600">
                                      <span>Paid: {data.totalPaid.toFixed(2)}</span>
                                      <span>Owes: {data.totalOwes.toFixed(2)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h5 className="font-semibold text-gray-700">Settlement Plan</h5>
                              <div className="bg-white rounded-lg p-3 space-y-2">
                                {settlements.map((settlement, idx) => (
                                  <div key={idx} className="flex items-center justify-between gap-2">
                                    <span className="text-red-600">{settlement.from}</span>
                                    <span className="text-gray-500">→</span>
                                    <span className="text-green-600">{settlement.to}</span>
                                    <span className="font-medium">
                                      {currency} {settlement.amount}
                                    </span>
                                  </div>
                                ))}
                                {settlements.length === 0 && (
                                  <p className="text-gray-500 text-center">No settlements needed</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const handleDeleteTrip = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const { data: expenses, error: expensesQueryError } = await supabase
        .from('expense_groups')
        .select('id')
        .eq('trip_id', id);

      if (expensesQueryError) throw expensesQueryError;

      if (expenses?.length > 0) {
        const groupIds = expenses.map(group => group.id);
        
        const { error: expensesDeleteError } = await supabase
          .from('expenses_table')
          .delete()
          .in('group_id', groupIds);

        if (expensesDeleteError) throw expensesDeleteError;
      }

      const { error: groupsError } = await supabase
        .from('expense_groups')
        .delete()
        .eq('trip_id', id);

      if (groupsError) throw groupsError;

      const { error: destError } = await supabase
        .from('destinations')
        .delete()
        .eq('trip_id', id);

      if (destError) throw destError;

      const { error: tripError } = await supabase
        .from('trips')
        .delete()
        .eq('id', id);

      if (tripError) throw tripError;

      navigate('/trips');
    } catch (error) {
      console.error('Error deleting trip:',error);
      alert('Failed to delete trip. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const deleteConfirmationDialog = (
    <div className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity ${showDeleteConfirm ? 'flex' : 'hidden'}`}>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">Delete Trip</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this trip? This will also delete all related expenses and expense groups. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={confirmDelete}
                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
              >
                Delete
              </button> 
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Add this function near other handler functions
  const handleDestinationDaysChange = async (destId, days) => {
    try {
      // Update the database
      const { error } = await supabase
        .from('destinations')
        .update({ stay_days: days })
        .eq('id', destId);

      if (error) throw error;

      // Update local state
      setDestinationDays(prev => ({
        ...prev,
        [destId]: days
      }));
    } catch (error) {
      console.error('Error updating destination days:', error);
    }
  };

  // Add this useEffect near other useEffects
  useEffect(() => {
    // Initialize map only if we're in destinations tab and map hasn't been initialized
    if (activeDetailTab === 'destinations' && !map && window.google && mapRef.current) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: 20.5937, lng: 78.9629 },
        zoom: 5,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      const directionsService = new window.google.maps.DirectionsService();
      const directionsRenderer = new window.google.maps.DirectionsRenderer({
        map: newMap,
        suppressMarkers: false,
      });

      setMap(newMap);
      setDirectionsService(directionsService);
      setDirectionsRenderer(directionsRenderer);
    }
  }, [activeDetailTab, map, mapRef.current]);

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group and all its expenses?')) return;
    try {
      // First delete all expenses associated with this group
      const { error: expensesError } = await supabase
        .from('expenses_table')
        .delete()
        .eq('group_id', groupId);
      
      if (expensesError) throw expensesError;

      // Then delete the group
      const { error: groupError } = await supabase
        .from('expense_groups')
        .delete()
        .eq('id', groupId);
      
      if (groupError) throw groupError;

      // Update local state
      setExpenseGroups(expenseGroups.filter(g => g.id !== groupId));
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setExpenses([]);
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Failed to delete group');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      const { error } = await supabase
        .from('expenses_table')
        .delete()
        .eq('id', expenseId);
      
      if (error) throw error;
      setExpenses(expenses.filter(e => e.id !== expenseId));
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  // Add new function to calculate balances
  const calculateBalances = (expenses, groupMembers) => {
    const expensesByCurrency = {};
    
    expenses.forEach(expense => {
      const currency = expense.currency || 'USD';
      if (!expensesByCurrency[currency]) {
        expensesByCurrency[currency] = [];
      }
      expensesByCurrency[currency].push(expense);
    });

    const result = {};
    
    Object.entries(expensesByCurrency).forEach(([currency, currencyExpenses]) => {
      const memberBalances = {};
      let totalExpenditure = 0;
      
      // Initialize member balances
      groupMembers.forEach(member => {
        memberBalances[member.email] = {
          name: member.name,
          totalPaid: 0,     // Total amount paid by this member
          totalOwes: 0,     // Total amount this member owes
          net: 0            // Net balance
        };
      });

      // Process each expense
      currencyExpenses.forEach(expense => {
        const amount = Number(expense.amount);
        totalExpenditure += amount;

        // Add to payer's total paid amount
        memberBalances[expense.paid_by].totalPaid += amount;

        if (expense.split_type === 'equal') {
          const splitAmount = amount / groupMembers.length;
          groupMembers.forEach(member => {
            memberBalances[member.email].totalOwes += splitAmount;
          });
        } else {
          // Handle unequal or percentage splits
          expense.splits.forEach(split => {
            memberBalances[split.user_email].totalOwes += Number(split.amount);
          });
        }
      });

      // Calculate net balances and prepare settlements
      const settlements = [];
      const debtors = [];
      const creditors = [];

      // Calculate final net amounts
      Object.entries(memberBalances).forEach(([email, balance]) => {
        balance.net = balance.totalPaid - balance.totalOwes;
        
        if (balance.net < 0) {
          debtors.push({
            email,
            name: balance.name,
            amount: Math.abs(balance.net)
          });
        } else if (balance.net > 0) {
          creditors.push({
            email,
            name: balance.name,
            amount: balance.net
          });
        }
      });

      // Calculate who pays whom
      debtors.forEach(debtor => {
        let remainingDebt = debtor.amount;
        
        creditors.forEach(creditor => {
          if (remainingDebt > 0 && creditor.amount > 0) {
            const settleAmount = Math.min(remainingDebt, creditor.amount);
            if (settleAmount > 0) {
              settlements.push({
                from: debtor.name,
                to: creditor.name,
                amount: settleAmount.toFixed(2)
              });
              remainingDebt -= settleAmount;
              creditor.amount -= settleAmount;
            }
          }
        });
      });

      result[currency] = {
        totalExpenditure,
        balances: memberBalances,
        settlements
      };
    });

    return result;
  };

  const handleEditGroup = (group) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.group_name);
  };

  const handleUpdateGroup = async (groupId) => {
    try {
      const { data, error } = await supabase
        .from('expense_groups')
        .update({ group_name: editGroupName })
        .eq('id', groupId)
        .select()
        .single();

      if (error) throw error;
      setExpenseGroups(groups => 
        groups.map(g => g.id === groupId ? data : g)
      );
      setEditingGroupId(null);
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpenseId(expense.id);
    setEditExpense({
      description: expense.description,
      amount: expense.amount.toString(),
      currency: expense.currency,
      split_type: expense.split_type
    });
  };

  const handleUpdateExpense = async (expenseId) => {
    try {
      const { data, error } = await supabase
        .from('expenses')
        .update({
          description: editExpense.description,
          amount: parseFloat(editExpense.amount),
          currency: editExpense.currency,
          split_type: editExpense.split_type
        })
        .eq('id', expenseId)
        .select()
        .single();

      if (error) throw error;
      setExpenses(expenses => 
        expenses.map(e => e.id === expenseId ? data : e)
      );
      setEditingExpenseId(null);
    } catch (error) {
      console.error('Error updating expense:', error);
    }
  };

  const generateNewItinerary = async () => {
    setIsGeneratingItinerary(true);
    setIsItineraryError(false);
    try {
      const totalDays = Math.ceil(
        (new Date(trip.end_date) - new Date(trip.start_date)) / (1000 * 60 * 60 * 24)
      );
      
      const response = await generateItinerary(destinations, totalDays, false);
      if (response) {
        setGeneratedItinerary(response);
        setIsEditingItinerary(true);
        
        const { error } = await supabase
          .from('trips')
          .update({ description: response })
          .eq('id', id);

        if (error) throw error;
        setTrip(prev => ({ ...prev, description: response }));
      }
    } catch (error) {
      console.error('Error generating itinerary:', error);
      setIsItineraryError(true);
      setGeneratedItinerary('');
      setIsEditingItinerary(false);
    } finally {
      setIsGeneratingItinerary(false);
    }
  };

  return isNew ? (
    <div className="min-h-screen bg-cover bg-fixed bg-center" 
      style={{ 
        backgroundImage: 'url("https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=2070&auto=format&fit=crop")',
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        backgroundBlend: 'overlay'
      }}>
      <Header />
      <AdScript />  {/* Add this line */}
      <main className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white/95 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-8">Create New Trip</h1>
            
            <form onSubmit={handleCreateTrip} className="space-y-6">
              <div>
                <label htmlFor="tripTitle" className="block text-sm font-medium text-gray-700">
                  Trip Title
                </label>
                <input
                  type="text"
                  id="tripTitle"
                  value={tripTitle}
                  onChange={(e) => setTripTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Place to Visit
                </label>
                <PlaceAutocomplete 
                  onPlaceSelect={(place) => {
                    if (place && place.geometry) {
                      setPlaceToVisit(place.formatted_address || place.name);
                      setSelectedPlace(place);
                    }
                  }} 
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm mt-2 text-center">{error}</div>
              )}

              <div className="flex justify-center space-x-3 pt-4">
                <Link
                  to="/"
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    isLoading 
                      ? 'bg-indigo-400 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Trip...
                    </>
                  ) : (
                    'Create Trip'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  ) : (
    <div className="min-h-screen bg-cover bg-fixed bg-center" 
      style={{ 
        backgroundImage: 'url("https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop")',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backgroundBlend: 'overlay'
      }}>
      <Header />
      <main className="max-w-[90rem] mx-auto py-6">
        <div className="bg-white/95 backdrop-blur-sm shadow-xl rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex justify-between items-center mb-8 max-w-7xl mx-auto">
              <h1 className="text-6xl font-bold text-indigo-600">{trip?.title}</h1>
              {trip && (
                <p className="text-lg text-gray-600">
                  Your trip is from {formatDate(trip.start_date)}
                  {trip.end_date !== trip.start_date && ` - ${formatDate(trip.end_date)}`}
                </p>
              )}
            </div>
            <div className="flex justify-end mb-4 max-w-7xl mx-auto">
              <button
                onClick={handleDeleteTrip}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete Trip
              </button>
            </div>
            <div className="w-full">
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-4 justify-center overflow-x-auto">
                    <button
                      onClick={() => setActiveDetailTab('destinations')}
                      className={`${
                        activeDetailTab === 'destinations'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Destinations
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('itinerary')}
                      className={`${
                        activeDetailTab === 'itinerary'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Itinerary
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('notes')}
                      className={`${
                        activeDetailTab === 'notes'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Notes
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('tips')}
                      className={`${
                        activeDetailTab === 'tips'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Tips & Recommendations
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('stays')}
                      className={`${
                        activeDetailTab === 'stays'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Stays
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('attractions')}
                      className={`${
                        activeDetailTab === 'attractions'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Popular Attractions
                    </button>
                    <button
                      onClick={() => setActiveDetailTab('expenses')}
                      className={`${
                        activeDetailTab === 'expenses'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                    >
                      Expenses
                    </button>
                  </nav>
                </div>
                <div className="p-6">
                  {renderDetailTabContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Chatbot />
      {deleteConfirmationDialog}
    </div>
  );
}